/**
 * P5 — Site: Strings and combiners (UX-002 §08, Set 4 wireframe).
 *
 * WHAT THIS SCREEN CAN SAY TODAY.
 *
 * The wireframe for this screen is a magnitude heat-map of per-string currents.
 * There is no endpoint that returns that matrix: `/v1/sites/{id}/strings` does
 * not exist, and `/v1/series` is one device + one signal per call, so the matrix
 * cannot be assembled here either (and a KPI/derivation must not be assembled in
 * the browser at all — CLAUDE.md rule 2).
 *
 * This screen therefore shows what IS on record — the blocks, and the string
 * layout the registry carries per inverter (`mppt_count` × `strings_per_mppt`)
 * — and WITHHOLDS everything else with the reason that names what is missing.
 * It previously generated the whole 10×24 current matrix from a hash of the row
 * and column index, including three planted "escapee" amperages and a zeroed
 * row, and presented it as live string current. Those numbers are gone: a
 * fabricated figure is worse than none (KPIDEF-001 §3.2/§6.1).
 *
 * When the string view-model lands, the withheld panel below is the only part
 * that changes — the matrix returns with real values, a real basis, and a real
 * reading age, all from the server.
 */
import { useState, type ReactNode } from "react";
import { PageHeader } from "../shell/PageHeader";
import { SiteTabBar } from "../components/SiteTabBar";
import {
  useGetV1SitesIdBlocks,
  useGetV1BlocksId,
  type EnvelopeBlockDetailDataDevicesItem,
} from "@solarkey/api-client";
import { EmptyState, Skeleton, ErrorState, StateWord } from "@solarkey/ui";

/* ── Helpers ──────────────────────────────────────────────────────────── */

function getBlockDisplay(
  b: { block_id?: string; label?: string },
  idx: number,
): { full: string; short: string } {
  if (b.label) {
    const parts = b.label.trim().split(/\s+/);
    return { full: b.label, short: parts[parts.length - 1] ?? b.label };
  }
  if (b.block_id) return { full: b.block_id, short: b.block_id };
  return { full: `Block ${idx + 1}`, short: String(idx + 1) };
}

/** Strings on record for one inverter: mppt_count × strings_per_mppt, or null.
 *  Null when either number is absent — it is never substituted by a constant. */
function stringsOnRecord(d: EnvelopeBlockDetailDataDevicesItem): number | null {
  const np = d.nameplate as Record<string, unknown> | undefined;
  const direct = np?.["string_count"];
  if (typeof direct === "number" && direct > 0) return direct;
  const mppt = np?.["mppt_count"];
  const per = np?.["strings_per_mppt"];
  if (typeof mppt === "number" && typeof per === "number" && mppt > 0 && per > 0) {
    return mppt * per;
  }
  return null;
}

/* ── Sub-components ───────────────────────────────────────────────────── */

function HeadlineTile(props: {
  label: string;
  children: ReactNode;
  sub?: ReactNode;
}): ReactNode {
  return (
    <div style={tileSt}>
      <span style={tileLabel}>{props.label}</span>
      {props.children}
      {props.sub ? <span style={tileSub}>{props.sub}</span> : null}
    </div>
  );
}

/** A withheld tile: the state word carries the reason, never a 0 or a dash. */
function WithheldTile(props: {
  label: string;
  state: "CANNOT_CHECK" | "NOT_MEASURED" | "NOT_APPLICABLE";
  template: string;
  sub?: ReactNode;
}): ReactNode {
  return (
    <HeadlineTile label={props.label} sub={props.sub}>
      <span style={tileVal}>
        <StateWord state={props.state} reason={{ template: props.template, params: {} }} />
      </span>
    </HeadlineTile>
  );
}

/* ── Main screen ──────────────────────────────────────────────────────── */

export function SiteStringsScreen(props: { siteId: string }): ReactNode {
  const [pickedBlock, setPickedBlock] = useState<string | null>(null);

  const blocksQ = useGetV1SitesIdBlocks(props.siteId);
  const blocks = blocksQ.data?.data?.data?.items ?? [];
  const activeBlockId = pickedBlock ?? blocks[0]?.block_id ?? null;
  const activeBlock = blocks.find((b) => b.block_id === activeBlockId) ?? blocks[0];
  const activeIdx = Math.max(0, blocks.findIndex((b) => b.block_id === activeBlockId));

  // The block drawer is the only source of real string layout: it carries each
  // inverter's registry nameplate (mppt_count / strings_per_mppt) and the
  // block's combiner devices.
  const detailQ = useGetV1BlocksId(
    activeBlockId ?? "",
    undefined,
    { query: { enabled: !!activeBlockId } },
  );
  const detail = detailQ.data?.status === 200 ? detailQ.data.data.data : undefined;
  const devices = detail?.devices ?? [];
  const inverters = devices.filter((d) => d.device_class === "inverter");
  const combiners = devices.filter((d) => d.device_class === "combiner");

  const perInverter = inverters.map(stringsOnRecord);
  const withLayout = perInverter.filter((n): n is number => n != null);
  const stringsTotal = withLayout.reduce((a, b) => a + b, 0);
  // Stated only when EVERY inverter in the block carries its string layout —
  // a partial sum would read as the block total.
  const stringsComplete = inverters.length > 0 && withLayout.length === inverters.length;

  const display = activeBlock
    ? getBlockDisplay(activeBlock, activeIdx)
    : { full: "", short: "" };

  if (blocksQ.isLoading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: 16 }}>
        <Skeleton variant="text" width="14rem" />
        <div style={headlineRow}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="tile" />
          ))}
        </div>
      </div>
    );
  }

  if (blocksQ.isError || !blocksQ.data) {
    return (
      <ErrorState
        title="Couldn't load strings"
        message="Block data is required for the string view."
        onRetry={() => void blocksQ.refetch()}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* No time control and no Export: neither is wired to anything on this
          screen, and a control that does nothing is a claim that it works. */}
      <PageHeader
        title={`Site ${props.siteId} · Strings and combiners`}
        scope={
          activeBlock
            ? `${display.full} · ${inverters.length} inverters on record`
            : `${blocks.length} blocks`
        }
        showTimeControl={false}
      />
      <SiteTabBar site={props.siteId} />

      {/* ── What this screen can and cannot say, before any figure ──── */}
      <EmptyState
        variant="no_data"
        title="Per-string currents are not served yet"
        message={
          "The per-string current matrix needs a strings view-model (/v1/sites/{id}/strings). " +
          "The underlying metrics exist in the catalogue (dc_current_string_N, mppt_N_current/voltage) " +
          "but only per device and per signal, so no screen can state a string current today. " +
          "Everything below comes from the estate registry; anything not on record is withheld with its reason."
        }
      />

      {/* ── Headline tiles — real from the registry, or withheld ─────── */}
      <div style={headlineRow}>
        {detailQ.isLoading ? (
          <>
            <Skeleton variant="tile" />
            <Skeleton variant="tile" />
            <Skeleton variant="tile" />
            <Skeleton variant="tile" />
          </>
        ) : (
          <>
            {stringsComplete ? (
              <HeadlineTile
                label="Strings on record"
                sub={`${inverters.length} inverters · registry string layout (MPPT × strings per MPPT)`}
              >
                <span style={tileVal}>{stringsTotal}</span>
              </HeadlineTile>
            ) : (
              <WithheldTile
                label="Strings on record"
                state="NOT_MEASURED"
                template="NOT_MEASURED_STRING_LAYOUT_NOT_IN_REGISTRY"
                sub={
                  inverters.length === 0
                    ? "no inverters resolved for this block"
                    : `string layout missing for ${inverters.length - withLayout.length} of ${inverters.length} inverters`
                }
              />
            )}

            {combiners.length > 0 ? (
              <HeadlineTile label="Combiners on record" sub="combiner devices in the estate registry">
                <span style={tileVal}>{combiners.length}</span>
              </HeadlineTile>
            ) : (
              <WithheldTile
                label="Combiners on record"
                state="NOT_MEASURED"
                template="NOT_MEASURED_NO_COMBINER_DEVICES_IN_REGISTRY"
                sub="no combiner device is registered for this block"
              />
            )}

            <WithheldTile
              label="String measurement basis"
              state="CANNOT_CHECK"
              template="CANNOT_CHECK_STRING_BASIS_NOT_ON_RECORD"
              sub="whether current is measured per string or per MPPT is not declared in the registry"
            />

            <WithheldTile
              label="String reading"
              state="NOT_MEASURED"
              template="NOT_MEASURED_NO_STRING_SERIES_ENDPOINT"
              sub="no string reading reaches this screen, so its age cannot be stated"
            />
          </>
        )}
      </div>

      {/* ── Block selector — real, and it drives the block drawer read ── */}
      <div style={toolbar}>
        <span style={toolbarLbl}>Block</span>
        <div style={chipGroup} role="group" aria-label="Select block">
          {blocks.map((b, i) => {
            const d = getBlockDisplay(b, i);
            return (
              <button
                key={b.block_id ?? i}
                type="button"
                style={b.block_id === activeBlockId ? chipAct : chipSt}
                aria-pressed={b.block_id === activeBlockId}
                onClick={() => setPickedBlock(b.block_id ?? null)}
              >
                {d.full}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Per-inverter string layout — the one real table on this screen ── */}
      {detailQ.isError ? (
        <ErrorState
          title="Couldn't load the block's devices"
          message="The string layout comes from the block drawer."
          onRetry={() => void detailQ.refetch()}
        />
      ) : detailQ.isLoading ? (
        <Skeleton variant="row" lines={6} />
      ) : inverters.length === 0 ? (
        <EmptyState
          variant="no_data"
          title="No inverters resolved for this block"
          message="Strings are held against the inverter in the registry."
        />
      ) : (
        <div style={matrixWrap}>
          <table style={matrixTable} aria-label="String layout on record">
            <thead>
              <tr>
                <th style={thLeft}>Inverter</th>
                <th style={thRight}>MPPTs</th>
                <th style={thRight}>Strings per MPPT</th>
                <th style={thRight}>Strings</th>
                <th style={thLeft}>Per-string current</th>
              </tr>
            </thead>
            <tbody>
              {inverters.map((d, i) => {
                const np = d.nameplate as Record<string, unknown> | undefined;
                const mppt = typeof np?.["mppt_count"] === "number" ? np["mppt_count"] : null;
                const per =
                  typeof np?.["strings_per_mppt"] === "number" ? np["strings_per_mppt"] : null;
                const total = perInverter[i] ?? null;
                return (
                  <tr key={d.device_id ?? i}>
                    <td style={tdLeft}>{d.role_name ?? d.device_id ?? `Inverter ${i + 1}`}</td>
                    <td style={tdRight}>
                      {mppt != null ? mppt : (
                        <StateWord
                          state="NOT_MEASURED"
                          reason={{ template: "NOT_MEASURED_MPPT_COUNT_NOT_IN_REGISTRY", params: {} }}
                        />
                      )}
                    </td>
                    <td style={tdRight}>
                      {per != null ? per : (
                        <StateWord
                          state="NOT_MEASURED"
                          reason={{ template: "NOT_MEASURED_STRINGS_PER_MPPT_NOT_IN_REGISTRY", params: {} }}
                        />
                      )}
                    </td>
                    <td style={tdRight}>
                      {total != null ? total : (
                        <StateWord
                          state="NOT_MEASURED"
                          reason={{ template: "NOT_MEASURED_STRING_LAYOUT_NOT_IN_REGISTRY", params: {} }}
                        />
                      )}
                    </td>
                    <td style={tdLeft}>
                      <StateWord
                        state="NOT_MEASURED"
                        reason={{ template: "NOT_MEASURED_NO_STRING_SERIES_ENDPOINT", params: {} }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p style={annotationText}>
        Counts are the estate registry&apos;s string layout (MPPT count × strings per MPPT) for
        the inverters in this block — not a measurement, and not multiplied out from an assumed
        strings-per-inverter figure. Current per string, deviation against a row median, escapee
        marking and relay state all wait on the strings view-model.
      </p>
    </div>
  );
}

/* ── Styles ────────────────────────────────────────────────────────────── */

const headlineRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: 12,
};
const tileSt: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  padding: "12px 14px",
  borderRadius: "var(--sk-radius-card, 8px)",
  border: "1px solid var(--sk-color-border-1)",
  background: "var(--sk-surface)",
};
const tileLabel: React.CSSProperties = {
  font: "600 10px/1.3 var(--sk-font-text)",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--sk-color-ink-3)",
};
const tileVal: React.CSSProperties = {
  font: "300 22px/1.15 var(--sk-font-mono, var(--sk-font-text))",
  color: "var(--sk-color-ink-1)",
};
const tileSub: React.CSSProperties = {
  font: "400 11px/1.3 var(--sk-font-text)",
  color: "var(--sk-color-ink-3)",
};

const toolbar: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 8,
};
const toolbarLbl: React.CSSProperties = {
  font: "400 12px/1.35 var(--sk-font-text)",
  color: "var(--sk-color-ink-3)",
};
const chipGroup: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
};
const chipSt: React.CSSProperties = {
  padding: "4px 10px",
  borderRadius: "var(--sk-radius-chip, 4px)",
  border: "1px solid var(--sk-color-border-1)",
  background: "var(--sk-surface)",
  color: "var(--sk-color-ink-2)",
  cursor: "pointer",
  font: "400 12px/1.35 var(--sk-font-text)",
};
const chipAct: React.CSSProperties = {
  ...chipSt,
  borderColor: "var(--sk-color-accent)",
  color: "var(--sk-color-accent)",
  fontWeight: 600,
};

const matrixWrap: React.CSSProperties = {
  overflowX: "auto",
  border: "1px solid var(--sk-color-border-1)",
  borderRadius: "var(--sk-radius-card, 8px)",
};
const matrixTable: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  font: "400 12px/1.4 var(--sk-font-text)",
};
const thLeft: React.CSSProperties = {
  padding: "6px 10px",
  textAlign: "left",
  font: "600 11px/1.3 var(--sk-font-text)",
  color: "var(--sk-color-ink-3)",
  borderBottom: "1px solid var(--sk-color-border-1)",
  whiteSpace: "nowrap",
};
const thRight: React.CSSProperties = { ...thLeft, textAlign: "right" };
const tdLeft: React.CSSProperties = {
  padding: "6px 10px",
  textAlign: "left",
  color: "var(--sk-color-ink-1)",
  borderBottom: "1px solid var(--sk-color-border-1)",
  whiteSpace: "nowrap",
};
const tdRight: React.CSSProperties = {
  ...tdLeft,
  textAlign: "right",
  font: "400 12px/1.4 var(--sk-font-mono, monospace)",
};

const annotationText: React.CSSProperties = {
  font: "400 11px/1.5 var(--sk-font-text)",
  color: "var(--sk-color-ink-3)",
  margin: 0,
};
